import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowDown, Loader2 } from "lucide-react";

import { AssistantMessage, type OpenCitationsRequest } from "@/components/assistant/AssistantMessage";
import { ChatInput } from "@/components/assistant/ChatInput";
import { ShareChatToggle } from "@/components/assistant/ShareChatToggle";
import { SourceDocumentChip } from "@/components/assistant/SourceDocumentChip";
import type { OpenDocumentRequest } from "@/components/assistant/types";
import { AppHeaderRail } from "@/components/common/AppHeaderRail";
import { ErrorBanner } from "@/components/common/ErrorBanner";
import { IconButton } from "@/components/ui/icon-button";
import { WorkspaceShell } from "@/components/workspace/WorkspaceShell";
import { WorkspaceTopBar } from "@/components/workspace/WorkspaceTopBar";
import { isEditAnnotation } from "@/components/assistant/types";
import { useChatHistory } from "@/contexts/ChatHistoryContext";
import { InspectorProvider, useInspector } from "@/contexts/InspectorContext";
import { WorklogProvider, type WorklogEntry } from "@/contexts/WorklogContext";
import { useAssistantChat } from "@/hooks/useAssistantChat";
import { markContentVisible } from "@/hooks/useNavPerf";
import type { ChatLiveSnapshot, ChatMessageRow } from "@/lib/api";
import { isWebCitation, type CitationObject, type WebCitationObject } from "@/lib/stream";

const SCROLL_TO_BOTTOM_THRESHOLD = 360;

function isAwayFromChatBottom(viewport: HTMLDivElement) {
  return viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight > SCROLL_TO_BOTTOM_THRESHOLD;
}

/**
 * Screen composition — Assistant
 *
 * Hero zone: message thread (center scrollable area, `max-w-3xl`).
 * Scanning regions (compact): sidebar chat history, inspector tab strip.
 * Focus regions (airy): message thread (`gap-6`, generous padding),
 *   composer (`max-w-3xl`, padded card), edit-review cards.
 */
interface ChatSurfaceProps {
  chatId?: string | null;
  projectId?: string;
  /** Route-loaded title used until chat history contains this chat. */
  initialTitle?: string | null;
  /** Provenance: the document this chat was started from (renders a "From: …" header chip). */
  sourceDocumentId?: string | null;
  initialRows?: ChatMessageRow[];
  /** In-flight turn snapshot from GET /chat/{id}; hydrates + tails the live stream on mount. */
  initialLive?: ChatLiveSnapshot;
  /** Header title shown in landing mode (no chatId). */
  landingTitle?: string;
  /** Header subtitle shown in landing mode. */
  landingSubtitle?: string;
  /** Hero heading shown above the input in landing mode. */
  heroHeading?: string;
  /** Hero description shown below the heading in landing mode. */
  heroDescription?: string;
  hasOlderMessages?: boolean;
  isLoadingOlderMessages?: boolean;
  onLoadOlderMessages?: () => void;
}

export function ChatSurface(props: ChatSurfaceProps) {
  return (
    <InspectorProvider>
      <ChatSurfaceInner {...props} />
    </InspectorProvider>
  );
}

function ChatSurfaceInner({
  chatId,
  projectId,
  initialTitle,
  sourceDocumentId,
  initialRows,
  initialLive,
  landingTitle = "Assistant",
  landingSubtitle = "Work with documents, edits, and citations in one conversation.",
  heroHeading = "What should Vadium handle?",
  heroDescription = "Ask for a review, comparison, draft, search, or tracked edit.",
  hasOlderMessages = false,
  isLoadingOlderMessages = false,
  onLoadOlderMessages
}: ChatSurfaceProps) {
  const { messages, isResponseLoading, reconnecting, error, submit, stop, editStatuses } = useAssistantChat({
    chatId: chatId ?? undefined,
    projectId,
    initialRows,
    initialLive,
    sameSurface: !chatId
  });
  const inspector = useInspector();
  const { chats } = useChatHistory();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const preserveScrollHeightRef = useRef<number | null>(null);
  // Mirrors state for synchronous layout-effect reads without retriggering the effect.
  const showScrollToBottomRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimeoutRef = useRef<number | null>(null);
  const cancelProgrammaticScrollRef = useRef<(() => void) | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Thin adapter: translate the assistant's OpenDocumentRequest into an
  // inspector tab. All right-pane kinds for a given document share one tab id
  // (`doc:${id}`), preserving the old "one tab per document" dedup behavior.
  const openDocument = useCallback(
    (request: OpenDocumentRequest) => {
      if (!request.documentId) return;
      const id = `doc:${request.documentId}`;
      const base = {
        documentId: request.documentId,
        filename: request.filename,
        versionId: request.versionId,
        versionNumber: request.versionNumber
      };
      if (request.kind === "edit") {
        inspector.openTab({ kind: "edit", id, title: request.filename, payload: { ...base, edit: request.edit } });
      } else if (request.kind === "citation") {
        inspector.openTab({ kind: "citation", id, title: request.filename, payload: { ...base, citation: request.citation } });
      } else {
        inspector.openTab({ kind: "document", id, title: request.filename, payload: base });
      }
    },
    [inspector]
  );

  const openCitations = useCallback(
    (request: OpenCitationsRequest) => {
      inspector.openTab({
        kind: "citations",
        id: "citations",
        title: "Citations",
        payload: request
      });
    },
    [inspector]
  );

  const worklogEntries: WorklogEntry[] = useMemo(
    () =>
      messages.flatMap((msg) =>
        msg.role === "assistant" && msg.events
          ? msg.events
              .filter((e) => e.type !== "content" && e.type !== "thinking")
              .map((event) => ({ messageId: msg.id, event }))
          : []
      ),
    [messages]
  );

  // Aggregate actionable citations across all finalized assistant messages and
  // keep the inspector context in sync so the Citations tab opened via the "+"
  // button shows the current chat's citations instead of an empty list.
  const chatCitations = useMemo(() => {
    const all: Array<CitationObject | WebCitationObject> = [];
    for (const msg of messages) {
      if (msg.role !== "assistant" || msg.isStreaming) continue;
      for (const a of msg.annotations ?? []) {
        if (isEditAnnotation(a)) continue;
        if (isWebCitation(a) ? Boolean(a.url?.trim()) : Boolean(a.document_id?.trim())) {
          all.push(a);
        }
      }
    }
    return all;
  }, [messages]);

  const { setChatCitations } = useInspector();
  useLayoutEffect(() => {
    setChatCitations(chatCitations);
  }, [chatCitations, setChatCitations]);

  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [workflowEnabled, setWorkflowEnabled] = useState(false);

  const isLanding = !chatId && messages.length === 0 && !isResponseLoading;

  const setScrollToBottomVisible = useCallback((visible: boolean) => {
    showScrollToBottomRef.current = visible;
    setShowScrollToBottom(visible);
  }, []);

  const loadOlderMessages = useCallback(() => {
    const viewport = scrollRef.current;
    if (!viewport || !hasOlderMessages || isLoadingOlderMessages) {
      return;
    }
    preserveScrollHeightRef.current = viewport.scrollHeight;
    onLoadOlderMessages?.();
  }, [hasOlderMessages, isLoadingOlderMessages, onLoadOlderMessages]);

  const handleMessageScroll = useCallback(() => {
    const viewport = scrollRef.current;
    if (!viewport || isProgrammaticScrollRef.current) {
      return;
    }
    setScrollToBottomVisible(isAwayFromChatBottom(viewport));
    if (viewport.scrollTop < 80) {
      loadOlderMessages();
    }
  }, [loadOlderMessages, setScrollToBottomVisible]);

  const scrollToBottom = useCallback(() => {
    const viewport = scrollRef.current;
    if (!viewport) {
      return;
    }
    cancelProgrammaticScrollRef.current?.();
    const finishScroll = () => {
      if (programmaticScrollTimeoutRef.current !== null) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
        programmaticScrollTimeoutRef.current = null;
      }
      viewport.removeEventListener("scrollend", finishScroll);
      cancelProgrammaticScrollRef.current = null;
      isProgrammaticScrollRef.current = false;
      setScrollToBottomVisible(isAwayFromChatBottom(viewport));
    };
    cancelProgrammaticScrollRef.current = () => {
      if (programmaticScrollTimeoutRef.current !== null) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
        programmaticScrollTimeoutRef.current = null;
      }
      viewport.removeEventListener("scrollend", finishScroll);
      cancelProgrammaticScrollRef.current = null;
      isProgrammaticScrollRef.current = false;
    };
    isProgrammaticScrollRef.current = true;
    setScrollToBottomVisible(false);
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
    viewport.addEventListener("scrollend", finishScroll, { once: true });
    // Fallback for browsers that do not reliably emit scrollend after smooth scroll.
    programmaticScrollTimeoutRef.current = window.setTimeout(() => {
      finishScroll();
    }, 700);
  }, [setScrollToBottomVisible]);

  useLayoutEffect(() => {
    const viewport = scrollRef.current;
    const previousHeight = preserveScrollHeightRef.current;
    if (!viewport || previousHeight === null || isLoadingOlderMessages) {
      return;
    }
    viewport.scrollTop += viewport.scrollHeight - previousHeight;
    preserveScrollHeightRef.current = null;
    setScrollToBottomVisible(isAwayFromChatBottom(viewport));
  }, [isLoadingOlderMessages, setScrollToBottomVisible]);

  useLayoutEffect(() => {
    const viewport = scrollRef.current;
    if (!viewport || preserveScrollHeightRef.current !== null) {
      return;
    }
    if (!showScrollToBottomRef.current) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    return () => cancelProgrammaticScrollRef.current?.();
  }, []);

  useEffect(() => {
    markContentVisible(!!initialRows?.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLanding) {
    return (
      <WorklogProvider entries={worklogEntries}>
        <div className="flex min-h-screen flex-col">
          <AppHeaderRail
            title={<h1 className="truncate text-lg font-semibold">{landingTitle}</h1>}
            description={landingSubtitle}
          />
          <section className="flex flex-1 items-center justify-center px-4 py-8">
            <div className="w-full max-w-3xl">
              <div className="mb-6 text-center">
                <h2 className="text-balance text-2xl font-semibold">{heroHeading}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{heroDescription}</p>
              </div>
              <ChatInput isLoading={false} onSubmit={(payload) => void submit(payload)} projectId={projectId} webSearchEnabled={webSearchEnabled} onWebSearchChange={setWebSearchEnabled} workflowEnabled={workflowEnabled} onWorkflowChange={setWorkflowEnabled} />
              {!projectId ? (
                <p className="mt-4 text-center text-xs text-muted-foreground">
                  Quick scratchpad — this chat isn&rsquo;t tied to a matter.{" "}
                  <Link to="/projects" className="font-medium underline underline-offset-2 hover:text-foreground">
                    Start in a matter
                  </Link>{" "}
                  to keep work organized and shareable with your team.
                </p>
              ) : null}
            </div>
          </section>
        </div>
      </WorklogProvider>
    );
  }

  const title = chatId ? chats.find((chat) => chat.id === chatId)?.title || initialTitle || "Untitled chat" : landingTitle;

  return (
    <WorklogProvider entries={worklogEntries}>
      <WorkspaceShell
        inspector
        defaultInspectorOpen={false}
        autoSaveId="vadium.panelSize.assistant"
        topBar={
          <WorkspaceTopBar
            title={title}
            actions={
              projectId ? (
                <>
                  {sourceDocumentId ? <SourceDocumentChip projectId={projectId} sourceDocumentId={sourceDocumentId} onOpen={openDocument} /> : null}
                  {chatId ? <ShareChatToggle chatId={chatId} projectId={projectId} /> : null}
                </>
              ) : null
            }
          />
        }
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="relative min-h-0 flex-1">
            <div ref={scrollRef} onScroll={handleMessageScroll} className="h-full overflow-y-auto px-4 py-6 md:px-8">
              <div className="mx-auto flex max-w-3xl flex-col gap-6">
                {hasOlderMessages ? (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={loadOlderMessages}
                      disabled={isLoadingOlderMessages}
                      className="rounded-md border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent disabled:opacity-60"
                    >
                      {isLoadingOlderMessages ? "Loading..." : "Load older"}
                    </button>
                  </div>
                ) : null}
                {messages.map((message) =>
                  message.role === "user" ? (
                    <div key={message.id} className="ml-auto max-w-[85%] rounded-md bg-primary px-4 py-3 text-sm leading-6 text-primary-foreground">
                      {message.content}
                    </div>
                  ) : (
                    <AssistantMessage key={message.id} message={message} onOpenDocument={openDocument} onOpenCitations={openCitations} editStatuses={editStatuses} />
                  )
                )}
                {reconnecting ? (
                  <div
                    data-testid="reconnecting-banner"
                    role="status"
                    className="flex items-center gap-2 rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-xs text-warning"
                  >
                    <Loader2 className="size-3.5 animate-spin" />
                    Connection interrupted — reconnecting…
                  </div>
                ) : null}
                {error ? <ErrorBanner message={error} /> : null}
              </div>
            </div>
            {showScrollToBottom ? (
              <IconButton
                variant="outline"
                size="lg"
                label="Scroll to latest message"
                onClick={scrollToBottom}
                className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 border bg-card shadow-md"
              >
                <ArrowDown className="size-5" />
              </IconButton>
            ) : null}
          </div>
          <div className="border-t bg-background/95 p-4 backdrop-blur">
            <div className="mx-auto max-w-3xl">
              <ChatInput onSubmit={(payload) => void submit(payload)} onStop={stop} isLoading={isResponseLoading} projectId={projectId} compact webSearchEnabled={webSearchEnabled} onWebSearchChange={setWebSearchEnabled} workflowEnabled={workflowEnabled} onWorkflowChange={setWorkflowEnabled} />
            </div>
          </div>
        </div>
      </WorkspaceShell>
    </WorklogProvider>
  );
}
